'use client';
import React, { useState, useEffect, useRef } from 'react';
import { changeCustomerAddress } from '@/app/api/shopify/customer/account/address/change';
import { shopifyStoreCountries } from '@/app/api/shopify/shopifyStoreCountries/shopifyStoreCountries';
import { useModal, MODAL_TYPES } from '../ModalProvider';

const AddressChangeModal = ({
  userId,
  defaultAddress,
  hideModal,
  isOpen,
  labels,
  addressId
}) => {
  const modalRef = useRef(null);
  const { modalUtils, getCloseButton } = useModal();
  const [input, setInput] = useState({});
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [countries, setCountries] = useState([]);
  const [listOfProvinces, setListOfProvinces] = useState([]);

  useEffect(() => {
    if (!defaultAddress) return;

    const country = countries.find(
      (country) => country.name === defaultAddress.country
    );

    const province = country?.provinces.find(
      (province) => province.name === defaultAddress.province
    );
    setShowErrorMsg(false);
    setInput({
      ...defaultAddress,
      country: country?.code || '',
      province: province?.code || ''
    });
  }, [defaultAddress, countries]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await shopifyStoreCountries();
        setCountries(response.data || []);
      } catch (error) {
        console.error('Error getting countries list', error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (input?.country) {
      setListOfProvinces(
        countries.find((country) => country.code === input.country)
          ?.provinces || []
      );
    } else {
      setListOfProvinces([]);
    }
  }, [input.country, countries]);

  useEffect(() => {
    if (!isOpen) return;
    const ref = modalRef.current;

    if (ref) {
      ref.focus();
      // Ensure modal content starts at the top
      ref.scrollTop = 0;
    }
  }, [isOpen, hideModal]);

  // Ensure modal scrolls to top when opened
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Find the modal container with overflow-auto
      const modalContainer = modalRef.current.closest('.overflow-auto');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      // Also ensure the modal body itself is at top
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setInput((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && {
        territoryCode: value,
        province: '',
        zoneCode: ''
      }),
      ...(name === 'province' && {
        zoneCode: value
      })
    }));
  };

  const handleSubmit = async () => {
    const addressChangeData = await changeCustomerAddress(
      input,
      userId,
      defaultAddress,
      addressId
    );
    if (addressChangeData?.success == true) {
      hideModal();
    }
    if (!addressChangeData?.success) {
      setShowErrorMsg(true);
      setErrorMsg(
        'Failed to update address. Please check your input and try again.'
      );
    }
  };

  const listItems = listOfProvinces.map((province, index) => (
    <option key={index} value={province.code}>
      {province.name}
    </option>
  ));

  if (!isOpen) return null;

  return modalUtils.createModalWrapper({
    modalType: MODAL_TYPES.addressChange,
    isOpen,
    onClose: hideModal,
    containerClassName:
      'animate-in fade-in zoom-in-95 relative max-h-[80vh] w-full max-w-xl overflow-auto border bg-white p-12 text-left text-black shadow-2xl transition-all duration-300 ease-out sm:max-w-2xl lg:max-h-screen font-lato',
    children: (
      <div
        ref={modalRef}
        className="modal-body"
        tabIndex={-1}
        data-dd-privacy="mask-user-input"
      >
        {getCloseButton(MODAL_TYPES.addressChange, hideModal)}
        <h5 className="pb-2 text-xl font-semibold">{labels.heading}</h5>
        <div>
          {showErrorMsg && (
            <div className="body-r text-red-700" role="alert">
              {errorMsg}
            </div>
          )}
          <form method="post">
            {[
              { name: 'address1', label: labels.address_line_one },
              { name: 'address2', label: labels.address_line_two },
              { name: 'city', label: labels.city },
              { name: 'company', label: labels.company },
              { name: 'firstName', label: labels.first_name },
              { name: 'lastName', label: labels.last_name },
              { name: 'phone', label: labels.phone },
              { name: 'zip', label: labels.zip }
            ].map(({ name, label }) => (
              <div key={name} className="pt-1 pr-2 pb-1 lg:flex">
                <div className="w-36 pt-1 pr-2 pb-1">{label}:</div>
                <div>
                  <input
                    type="text"
                    name={name}
                    className="input-primary w-[199px]"
                    value={input[name] || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            ))}
            <div className="pt-1 pr-2 pb-1 lg:flex">
              <div className="w-36 pt-1 pr-2 pb-1">{labels.country}:</div>
              <div>
                <select
                  className="input-primary min-h-[36px] w-[199px]"
                  name="country"
                  value={input.country || 'select a country'}
                  onChange={handleChange}
                >
                  <option value="select a country">
                    {labels.country_select_label}
                  </option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pt-1 pr-2 pb-1 lg:flex">
              <div className="w-36 pt-1 pr-2 pb-1">{labels.province}:</div>
              <div className="min-w-[199px]">
                <select
                  className="input-primary min-h-[36px] w-[199px]"
                  name="province"
                  value={input.zoneCode || 'select a province'}
                  onChange={handleChange}
                >
                  <option value="select a province">Select a province</option>
                  {listItems}
                </select>
              </div>
            </div>
          </form>
          <div className="mx-auto flex">
            <button
              className="btn btn-primary mx-auto mt-8 inline-block max-w-[200px]"
              onClick={handleSubmit}
            >
              {labels.button_label}
            </button>
          </div>
        </div>
      </div>
    )
  });
};

export default AddressChangeModal;
